import moment from "moment";
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
} from "react";
import { Spinner, Tooltip } from "reactstrap";
import {
  Download,
  PlayBtn,
  Check2,
  Check2All,
  Dot,
} from "react-bootstrap-icons";
import ProgressiveImage from "./ProgressiveImage";
import { useSendbird } from "@sendbird/uikit-react";
import { useGroupChannelContext } from "@sendbird/uikit-react/GroupChannel/context";
import EmojiIcon from "../../../../../assets/images/emoji.svg";
import styles from "./customMessage.module.scss";
import { getSafeThumbnailUrl } from "Messenger/pages/TeamConversation/components/SendbirdCustomComponents/sendbirdMedia.helpers";
import { formatLocalDateTime } from "utils";
const REACTION_OPTIONS = [
  { key: "sendbird_emoji_thumbsup", emoji: "👍" },
  { key: "sendbird_emoji_thumbsdown", emoji: "👎" },
  { key: "sendbird_emoji_heart", emoji: "❤️" },
  { key: "sendbird_emoji_thanking", emoji: "🙏" },
  { key: "sendbird_emoji_smile", emoji: "😊" },
  { key: "sendbird_emoji_surprised", emoji: "😲" },
  { key: "sendbird_emoji_sad", emoji: "😢" },
];

// Function to convert text with URLs and emails into clickable links
const renderTextWithLinks = (text) => {
  if (!text) return text;

  const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
  const urlRegex =
    /(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9-]+\.[a-zA-Z]{2,}[^\s]*)/g;
  const parts = [];
  let lastIndex = 0;
  const emailMatches = [];
  let emailMatch;
  while ((emailMatch = emailRegex.exec(text)) !== null) {
    const matchIndex = emailMatch.index;
    const matchLength = emailMatch[0].length;
    const matchEmail = emailMatch[0];
    emailMatches.push({
      index: matchIndex,
      length: matchLength,
      email: matchEmail,
      type: "email",
    });
  }
  const urlMatches = [];
  let urlMatch;
  while ((urlMatch = urlRegex.exec(text)) !== null) {
    const matchIndex = urlMatch.index;
    const matchLength = urlMatch[0].length;
    const matchUrl = urlMatch[0];
    const isExactEmail = emailMatches.some(
      (em) => em.index === matchIndex && em.length === matchLength,
    );
    const isInsideEmail = emailMatches.some(
      (em) =>
        matchIndex < em.index + em.length &&
        matchIndex + matchLength > em.index,
    );
    if (!isExactEmail && !isInsideEmail) {
      urlMatches.push({
        index: matchIndex,
        length: matchLength,
        url: matchUrl,
        type: "url",
      });
    }
  }
  const allMatches = [...emailMatches, ...urlMatches].sort(
    (a, b) => a.index - b.index,
  );
  allMatches.forEach((match) => {
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    if (match.type === "email") {
      const email = match.email;
      parts.push(
        <a
          key={match.index}
          href={`mailto:${email}`}
          style={{ textDecoration: "underline" }}
        >
          {email}
        </a>,
      );
    } else {
      let url = match.url;
      let displayUrl = url;
      if (!url.startsWith("http://") && !url.startsWith("https://")) {
        url = "https://" + url;
      }
      if (displayUrl.length > 50) {
        displayUrl = displayUrl.substring(0, 50) + "...";
      }
      parts.push(
        <a
          key={match.index}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: "underline" }}
        >
          {displayUrl}
        </a>,
      );
    }

    lastIndex = match.index + match.length;
  });
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }
  return parts.length > 0 ? parts : text;
};
export const isFailedMessage = (message) => message?.sendingStatus === "failed";
export const isPendingMessage = (message) =>
  message?.sendingStatus === "pending";

export function MessageEmojiMenu({ message, onEmojiClick }) {
  const {
    state: { config },
  } = useSendbird();
  const { toggleReaction } = useGroupChannelContext();
  const menuRef = useRef(null);

  // Dynamic positioning state
  const [menuStyle, setMenuStyle] = useState({
    position: "absolute",
    top: 0,
    left: 0,
    background: "white",
    padding: "12px",
    borderRadius: 50,
    zIndex: 100,
    opacity: 0,
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
  });

  // Calculate position on mount to prevent cutting off
  useLayoutEffect(() => {
    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const windowWidth = window.innerWidth;

      let newLeft = 0;

      // Check if menu goes off the right edge
      if (rect.right > windowWidth - 20) {
        const overflow = rect.right - (windowWidth - 20);
        newLeft = -overflow;
      }
      if (rect.left < 20) {
        newLeft = 20 - rect.left;
      }

      setMenuStyle((prev) => ({
        ...prev,
        left: newLeft,
        opacity: 1,
      }));
    }
  }, []);

  if (isPendingMessage(message) || isFailedMessage(message)) {
    return null;
  }

  return (
    <div
      ref={menuRef}
      className={`${styles["emoji-reaction-menu"]} d-flex gap-xxs`}
      style={menuStyle}
    >
      {REACTION_OPTIONS.map((emoji) => {
        const isReacted =
          message?.reactions
            ?.find((reaction) => reaction.key === emoji.key)
            ?.userIds?.some((reactorId) => reactorId === config.userId) ||
          false;
        return (
          <div
            key={emoji.key}
            className={` ${styles["emoji-reaction-item"]} ${
              isReacted ? "reacted" : ""
            }`}
            onClick={() => {
              if (!isReacted) {
                const alreadyReacted = message?.reactions.find((res) =>
                  res.userIds?.some((reactorId) => reactorId === config.userId),
                );
                if (alreadyReacted?.key) {
                  toggleReaction(message, alreadyReacted.key, true);
                }
              }
              toggleReaction(message, emoji.key, isReacted);
              onEmojiClick();
            }}
            testID={`ui_emoji_reactions_menu_${emoji.key}`}
          >
            {emoji?.emoji}
          </div>
        );
      })}
    </div>
  );
}

const CustomMessageItem = (props) => {
  const {
    message,
    currentUser,
    deleteMessage,
    updateUserMessage,
    setFileMessage,
    setFileViewer,
    currentChannel,
    externalTabActive,
    t,
  } = props;
  const reactionRef = useRef(null);

  // FIX: Create a persistent ref object to hold all reaction badge elements
  const reactionBadgeRefs = useRef({});

  // Ref for the message area to calculate width dynamically
  const messageAreaRef = useRef(null);
  const [messageAreaWidth, setMessageAreaWidth] = useState("auto");

  const [, selectedMessage] = useState(false);
  const [, setShowVerticalDots] = useState(false);
  const [showMessageOptions, setShowMessageOptions] = useState(false);
  const [showEditInput, setShowEditInput] = useState(false);
  const [updatedMessage, setUpdatedMessage] = useState("");
  const [playVideo, setPlayVideo] = useState(false);
  const [showEmojiMenu, setShowEmojiMenu] = useState(false);
  const [hoveredReactionKey, setHoveredReactionKey] = useState(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (reactionRef.current && !reactionRef.current.contains(event.target)) {
        setShowEmojiMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  useLayoutEffect(() => {
    const updateWidth = () => {
      if (messageAreaRef.current) {
        setMessageAreaWidth(messageAreaRef.current.offsetWidth);
      }
    };
    updateWidth();
    const observer = new ResizeObserver(() => {
      updateWidth();
    });

    if (messageAreaRef.current) {
      observer.observe(messageAreaRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [message, showEditInput]);
  const {
    state: { config: sendbirdConfig },
  } = useSendbird();
  const { toggleReaction } = useGroupChannelContext();

  const {
    createdAt,
    updatedAt,
    customType,
    messageType,
    message: messageText,
    messageId,
    name,
    url,
    sender,
    data,
    sendingStatus,
    thumbnails,
  } = message;
  const thumbnailUrl = getSafeThumbnailUrl(thumbnails);
  let officeName = "";
  let additionalData;

  if (data) {
    try {
      additionalData = JSON.parse(data);
      officeName = additionalData.officeName;
    } catch {}
  }

  const handleMouseOver = () => {
    setShowVerticalDots(true);
  };

  const handleMouseLeave = () => {
    !showMessageOptions && setShowVerticalDots(false);
    setPlayVideo(false);
  };

  const handleDeleteMessage = async () => {
    setShowMessageOptions(false);
    try {
      await deleteMessage(currentChannel, message);
    } catch {}
  };

  const handleTextOnChange = (e) => {
    const value = e.target.value;
    setUpdatedMessage(value);
  };

  const handleEditClick = () => {
    setUpdatedMessage(messageText);
    setShowEditInput(true);
    setShowMessageOptions(false);
  };

  const handleEditCancel = () => {
    setShowEditInput(false);
  };

  const handleEditMessageSave = async () => {
    try {
      const userMessageParams = {};
      userMessageParams.message = updatedMessage;
      let msgData = {};
      if (additionalData) {
        msgData = {
          ...additionalData,
        };
      }
      msgData.isEdited = true;
      userMessageParams.data = JSON.stringify(msgData);
      await updateUserMessage(currentChannel, messageId, userMessageParams);
    } catch {}
  };

  const handleImageClick = () => {
    setFileMessage(message);
    setFileViewer(true);
  };

  const { userId } = sender;
  const isOwnMessage = userId === currentUser?.id?.toString();
  const isTextMessage = messageType === "user";
  const isEdited = updatedAt > 0;
  const sending = sendingStatus === "pending";
  const sent = sendingStatus === "succeeded";
  let unreadMemberCount;
  if (isOwnMessage) {
    unreadMemberCount = currentChannel.getUnreadMemberCount(message);
  }

  const existingReactions = useMemo(() => {
    if (!message?.reactions || message.reactions.length === 0) return [];
    return message.reactions.map((reaction) => {
      // Find the emoji object by key
      const emoji = REACTION_OPTIONS?.find((e) => e.key === reaction.key);
      return {
        key: reaction.key,
        userIds: reaction.userIds || [],
        count: reaction.userIds?.length || 0,
        emojiUrl: emoji?.emoji,
        emojiKey: emoji?.key,
      };
    });
  }, [message?.reactions, REACTION_OPTIONS]);

  // Calculate collective total count
  const totalReactionCount = useMemo(() => {
    return existingReactions.reduce((acc, curr) => acc + curr.count, 0);
  }, [existingReactions]);

  // Map of userId -> nickname for showing reactor names (from channel members)
  const nicknamesMap = useMemo(() => {
    const map = {};
    const members = currentChannel?.members || [];
    members.forEach((member) => {
      if (member?.userId) {
        map[member.userId] = member.nickname || member.userId;
      }
    });
    return map;
  }, [currentChannel?.members]);
  const handleReactionClick = (reactionKey) => {
    const reaction = message?.reactions?.find((r) => r.key === reactionKey);
    const isReacted =
      reaction?.userIds?.some(
        (reactorId) => reactorId === sendbirdConfig.userId,
      ) || false;
    toggleReaction(message, reactionKey, isReacted);
  };

  let content = null;
  let editContent = null;

  if (messageType === "user") {
    content = (
      <div className="text-box">
        {renderTextWithLinks(messageText)}
        {!isEdited ? null : <span className="edited-flag">{" (edited)"}</span>}
      </div>
    );
    editContent = (
      <>
        <div className="textarea-container">
          <textarea
            onChange={handleTextOnChange}
            name=""
            id=""
            cols="30"
            rows="5"
            placeholder="Edit message"
            value={updatedMessage}
          ></textarea>
        </div>
        <div className="edit-actions-container">
          <span
            onClick={handleEditCancel}
            className="edit-action-btn cancel-edit-btn"
          >
            Cancel
          </span>
          <span onClick={handleEditMessageSave} className="edit-action-btn">
            Save
          </span>
        </div>
      </>
    );
  }
  if (customType === "image") {
    content = (
      <>
        <div className="image-box cursor-pointer" onClick={handleImageClick}>
          <ProgressiveImage
            src={thumbnailUrl || url}
            alt={name}
            sending={sending}
          />
        </div>
        <div>{name}</div>
      </>
    );
  }
  if (customType === "document") {
    content = (
      <>
        <span className="document-name">
          <a href={url}>
            <Download /> {name}
          </a>
        </span>
      </>
    );
  }
  if (customType === "video") {
    content = (
      <>
        <div className="video-container">
          {thumbnailUrl && !playVideo && (
            <div onClick={() => setPlayVideo(true)}>
              <img src={thumbnailUrl} alt={name} />
              <PlayBtn className="video-play-btn" />
            </div>
          )}
          {(!thumbnailUrl || playVideo) && <video src={url} controls />}
        </div>
        <span>{name}</span>
      </>
    );
  }

  return (
    <div
      onMouseOver={handleMouseOver}
      onMouseLeave={handleMouseLeave}
      id={messageId}
      className={`${isOwnMessage ? "own-message-box" : "other-message-box"}`}
    >
      {sending && (
        <div className="loader-container">
          <Spinner animation="border" className="loader-spinner" />
        </div>
      )}
      <div
        className={`${styles["message-box-wrapper"]}`}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: isOwnMessage ? "flex-end" : "flex-start",
          width: "100%",
        }}
      >
        <div
          ref={messageAreaRef} // Ref added here to measure width
          className={`message-area ${
            isOwnMessage ? "outgoing-msg" : "incoming-msg"
          } ${selectedMessage ? "highlighted-message" : ""}`}
        >
          {!sending && isOwnMessage && (
            <div className={`message-action-options`}>
              {showMessageOptions && (
                <div className="message-options">
                  <ul>
                    <li onClick={handleDeleteMessage}>Delete</li>
                    {isTextMessage && <li onClick={handleEditClick}>Edit</li>}
                    {!isTextMessage && (
                      <li>
                        <a href={url}>{t("messenger.download")}</a>
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
          <div className="message-date">
            {formatLocalDateTime(createdAt, "MMM D, YYYY  ")}
            <Dot />
            {formatLocalDateTime(createdAt, "hh:mm A ")}
          </div>
          <div></div>
          {showEditInput ? editContent : content}
          {isOwnMessage && sent && (
            <div className="seen-msg-icon">
              {unreadMemberCount === 0 ? (
                <Check2All className="double-tick" />
              ) : (
                <Check2 className="single-tick" />
              )}
            </div>
          )}
          {(externalTabActive || currentChannel?.memberCount > 2) && (
            <div className="name-office-box">
              <span className="name-box">
                {isOwnMessage ? "You" : sender.nickname}
              </span>
              {externalTabActive && !!officeName && !isOwnMessage && (
                <span className="office-box">{officeName}</span>
              )}
            </div>
          )}
        </div>
        {!sending && (
          <div
            className={`${styles["message-reactions-container"]}`}
            style={{
              display: "flex",
              marginTop: "4px",
              width:
                isOwnMessage && messageAreaWidth ? messageAreaWidth : "100%",
              justifyContent: "flex-start",
            }}
          >
            {existingReactions.length > 0 && (
              <div className={`${styles["existing-reactions"]}`}>
                {existingReactions.map((reaction) => {
                  const isReacted = reaction.userIds?.some(
                    (reactorId) => reactorId === sendbirdConfig.userId,
                  );
                  const reactorNames = (reaction.userIds || [])
                    .map((uid) =>
                      uid === currentUser?.id?.toString()
                        ? "You"
                        : nicknamesMap[uid] || uid,
                    )
                    .filter(Boolean);
                  const reactionTooltipId = `reaction-${messageId}-${reaction.key}`;

                  return (
                    <React.Fragment key={reaction.key}>
                      <span
                        // FIX: Store the DOM element in the persistent refs object
                        ref={(el) =>
                          (reactionBadgeRefs.current[reaction.key] = el)
                        }
                        id={reactionTooltipId}
                        className={`cursor-pointer reaction-badge ${
                          isReacted ? "reacted" : ""
                        }`}
                        onClick={(e) => {
                          if (!isReacted) {
                            const alreadyReacted = message?.reactions.find(
                              (res) =>
                                res.userIds?.some(
                                  (reactorId) =>
                                    reactorId === sendbirdConfig.userId,
                                ),
                            );
                            if (alreadyReacted?.key) {
                              toggleReaction(message, alreadyReacted.key, true);
                            }
                          }
                          e.stopPropagation();
                          handleReactionClick(reaction.key);
                        }}
                        onMouseEnter={() => setHoveredReactionKey(reaction.key)}
                        onMouseLeave={() => setHoveredReactionKey(null)}
                      >
                        {reaction?.emojiUrl ? (
                          reaction?.emojiUrl
                        ) : (
                          <span>{reaction.key}</span>
                        )}
                      </span>
                      {reactorNames.length > 0 && (
                        <Tooltip
                          placement="top"
                          isOpen={hoveredReactionKey === reaction.key}
                          // FIX: Retrieve the element from the persistent refs object
                          target={() => reactionBadgeRefs.current[reaction.key]}
                          toggle={() => setHoveredReactionKey(null)}
                        >
                          {reactorNames.join(", ")}
                        </Tooltip>
                      )}
                    </React.Fragment>
                  );
                })}
                {/* ADDED COLLECTIVE COUNT AT THE END */}
                <span
                  style={{
                    marginLeft: "4px",
                    alignSelf: "center",
                    fontSize: "13px",
                    fontWeight: "500",
                    transform: "translate(3px, 1px)",
                  }}
                >
                  {totalReactionCount}
                </span>
              </div>
            )}
            <div
              className={`${styles["reaction-button-container"]} position-relative`}
              ref={reactionRef}
            >
              <img
                src={EmojiIcon}
                alt="emoji"
                width="20px"
                height="20px"
                style={{
                  verticalAlign: "middle",
                  marginRight: "4px",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowEmojiMenu((prev) => !prev);
                }}
              />

              {showEmojiMenu && (
                <div className="emoji-menu-wrapper">
                  <MessageEmojiMenu
                    message={message}
                    onEmojiClick={() => {
                      setShowEmojiMenu((prev) => !prev);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomMessageItem;
